import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { telegramService } from '../../services/telegram.service'
import api from '../../services/api'
import toast from 'react-hot-toast'

interface CommentOpportunity {
  id: number
  channel_id: number
  channel_title: string
  post_id: number
  post_text: string
  post_date: string
  relevance_score: number
  suggested_comment: string | null
  status: string
  created_at: string
}

interface CommentStats {
  total_opportunities: number
  pending: number
  commented: number
  skipped: number
  today_comments: number
}

export default function CommentsPage() {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState<string>('pending')

  // Check Telegram status
  const { data: telegramStatus } = useQuery({
    queryKey: ['telegramStatus'],
    queryFn: () => telegramService.getStatus(),
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['commentStats'],
    queryFn: async () => {
      const response = await api.get<CommentStats>('/comments/stats')
      return response.data
    },
    enabled: telegramStatus?.connected,
  })

  // Fetch opportunities
  const { data: opportunities = [], isLoading } = useQuery({
    queryKey: ['commentOpportunities', filter],
    queryFn: async () => {
      const response = await api.get<CommentOpportunity[]>('/comments/opportunities', {
        params: { status: filter !== 'all' ? filter : undefined },
      })
      return response.data
    },
    enabled: telegramStatus?.connected,
  })

  // Post comment mutation
  const postCommentMutation = useMutation({
    mutationFn: async ({ opportunityId, comment }: { opportunityId: number; comment: string }) => {
      const response = await api.post(`/comments/opportunities/${opportunityId}/post`, { comment })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commentOpportunities'] })
      queryClient.invalidateQueries({ queryKey: ['commentStats'] })
      toast.success('Комментарий отправлен')
    },
    onError: () => {
      toast.error('Ошибка при отправке комментария')
    },
  })

  // Skip mutation
  const skipMutation = useMutation({
    mutationFn: async (opportunityId: number) => {
      const response = await api.post(`/comments/opportunities/${opportunityId}/skip`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commentOpportunities'] })
      queryClient.invalidateQueries({ queryKey: ['commentStats'] })
    },
  })

  if (!telegramStatus?.connected) {
    return (
      <div className="pt-14">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Комментарии</h1>
          <p className="text-gray-600 mt-1">
            Авто-комментирование постов в каналах
          </p>
        </div>

        <div className="card text-center py-12">
          <div className="text-6xl mb-4">📱</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Telegram не подключён
          </h3>
          <p className="text-gray-600 mb-4">
            Подключите Telegram аккаунт для авто-комментирования
          </p>
          <a href="/settings" className="btn-primary inline-block">
            Подключить Telegram
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-14">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Комментарии</h1>
          <p className="text-gray-600 mt-1">
            Возможности для комментирования в каналах
          </p>
        </div>
        <button className="btn-primary">
          🔍 Найти возможности
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard label="Всего" value={stats.total_opportunities} />
          <StatCard label="Ожидают" value={stats.pending} color="yellow" />
          <StatCard label="Отправлено" value={stats.commented} color="green" />
          <StatCard label="Пропущено" value={stats.skipped} color="gray" />
          <StatCard label="Сегодня" value={stats.today_comments} color="blue" />
        </div>
      )}

      {/* Filter */}
      <div className="card mb-6">
        <div className="flex gap-2">
          {[
            { value: 'pending', label: 'Ожидают' },
            { value: 'commented', label: 'Отправлены' },
            { value: 'skipped', label: 'Пропущены' },
            { value: 'all', label: 'Все' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setFilter(option.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === option.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Opportunities List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : opportunities.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Нет возможностей для комментирования
          </h3>
          <p className="text-gray-600">
            Добавьте каналы и система найдёт релевантные посты
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {opportunities.map((opp) => (
            <OpportunityCard
              key={opp.id}
              opportunity={opp}
              onPost={(comment) => postCommentMutation.mutate({ opportunityId: opp.id, comment })}
              onSkip={() => skipMutation.mutate(opp.id)}
              isPosting={postCommentMutation.isPending}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function StatCard({
  label,
  value,
  color = 'gray',
}: {
  label: string
  value: number
  color?: string
}) {
  const colorClasses: Record<string, string> = {
    gray: 'bg-gray-50 text-gray-900',
    yellow: 'bg-yellow-50 text-yellow-900',
    green: 'bg-green-50 text-green-900',
    blue: 'bg-blue-50 text-blue-900',
  }

  return (
    <div className={`rounded-lg p-3 ${colorClasses[color]}`}>
      <p className="text-xs opacity-75">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  )
}

function OpportunityCard({
  opportunity,
  onPost,
  onSkip,
  isPosting,
}: {
  opportunity: CommentOpportunity
  onPost: (comment: string) => void
  onSkip: () => void
  isPosting: boolean
}) {
  const [comment, setComment] = useState(opportunity.suggested_comment || '')
  const [isEditing, setIsEditing] = useState(false)

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    commented: 'bg-green-100 text-green-800',
    skipped: 'bg-gray-100 text-gray-800',
  }

  const statusLabels: Record<string, string> = {
    pending: 'Ожидает',
    commented: 'Отправлен',
    skipped: 'Пропущен',
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{opportunity.channel_title}</span>
          <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[opportunity.status]}`}>
            {statusLabels[opportunity.status]}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-medium ${
              opportunity.relevance_score >= 80
                ? 'text-green-600'
                : opportunity.relevance_score >= 60
                ? 'text-yellow-600'
                : 'text-gray-600'
            }`}
          >
            {opportunity.relevance_score}% релевантность
          </span>
          <span className="text-xs text-gray-500">{formatDate(opportunity.post_date)}</span>
        </div>
      </div>

      {/* Post Text */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        <p className="text-sm text-gray-700 line-clamp-3">{opportunity.post_text}</p>
      </div>

      {/* Comment Section */}
      {opportunity.status === 'pending' && (
        <div className="space-y-3">
          {isEditing ? (
            <div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="input w-full"
                placeholder="Введите комментарий..."
              />
              <div className="flex justify-end gap-2 mt-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800"
                >
                  Отмена
                </button>
                <button
                  onClick={() => {
                    onPost(comment)
                    setIsEditing(false)
                  }}
                  disabled={isPosting || !comment.trim()}
                  className="btn-primary text-sm"
                >
                  {isPosting ? 'Отправка...' : 'Отправить'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {opportunity.suggested_comment && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-blue-600 mb-1">Предложенный комментарий:</p>
                  <p className="text-sm text-blue-900">{opportunity.suggested_comment}</p>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => onPost(opportunity.suggested_comment || '')}
                  disabled={isPosting || !opportunity.suggested_comment}
                  className="btn-primary text-sm flex-1"
                >
                  ✓ Отправить
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                  ✏️ Редактировать
                </button>
                <button
                  onClick={onSkip}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                >
                  Пропустить
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
